import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Typography,
    Container,
    Paper,
    Grid,
    LinearProgress,
    Chip,
    CircularProgress
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Close as CloseIcon,
    EventAvailable as EventIcon,
    Schedule as ScheduleIcon,
    ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import 'dayjs/locale/en';
import 'dayjs/locale/ar';

import { useSalonPublicTheme } from '../../../context/SalonPublicThemeContext';
import {
    useAppointmentsAvailableSlotsRetrieveQuery,
    useAppointmentsMultiConsecutiveSlotsCreateMutation
} from '../../../services/GLOWClient';
import { HIGH_DENSITY_TABLET_MEDIA } from '../../../utils/responsiveMedia';
import Button from '../../common/salonPublicAppearance/SalonPublicButton';

const DateTimeSelectionStep = ({
    salonId,
    salonDetails,
    selectedServices = [],
    employees = [],
    onContinue,
    onBack,
    onCancel,
    showBackButton = true,
    currentStep = 4,
    totalSteps = 5
}) => {
    const { t, i18n } = useTranslation();
    const { tokens: theme } = useSalonPublicTheme();
    
    // Configure dayjs locale based on current language
    useEffect(() => {
        const dayjsLocale = i18n.language === 'ar' ? 'ar' : i18n.language === 'en' ? 'en' : 'fr';
        dayjs.locale(dayjsLocale);
    }, [i18n.language]);
    const [selectedDate, setSelectedDate] = useState(dayjs());
    const [selectedSlot, setSelectedSlot] = useState(null); // string HH:mm or chain object
    const [multiSlots, setMultiSlots] = useState([]); // Array of chains for multi service
    const [loadingMulti, setLoadingMulti] = useState(false);

    // --- Logic : Single vs Multi ---
    const isMulti = selectedServices.length > 1;

    // --- Fetching Single Service Slots ---
    const singleServiceId = !isMulti ? selectedServices[0]?.service_id : null;
    const singleTeamMember = !isMulti ? selectedServices[0]?.teamMember : null; // ID or null (if 'any')

    // Query for single service
    const {
        data: singleSlotsData,
        isFetching: isFetchingSingle
    } = useAppointmentsAvailableSlotsRetrieveQuery(
        {
            serviceId: singleServiceId,
            teamMemberId: singleTeamMember === 'any' ? undefined : singleTeamMember,
            date: selectedDate.format("YYYY-MM-DD")
        },
        {
            skip: isMulti || !singleServiceId,
            refetchOnMountOrArgChange: true
        }
    );

    // --- Fetching Multi Service Slots ---
    const [createMultiConsecutiveSlots] = useAppointmentsMultiConsecutiveSlotsCreateMutation();

    // Helper to resolve team member ID (legacy logic emulation)
    const resolveEffectiveTeamMember = (serviceId, preferredId) => {
        if (!serviceId || !employees.length) return null;

        // Check specific ID preference
        if (preferredId && preferredId !== 'any') {
            const pId = Number(preferredId);
            const member = employees.find(m => (m.team_member_id === pId || m.id === pId));
            // Assuming loose compatibility check or trust the selection
            if (member) return pId;
        }

        // Fallback: Pick a random compatible member
        const eligible = employees.filter(m => {
            // Compatibility check using service_ids
            if (!m.service_ids) return true; // Assume all services if not defined ??
            return m.service_ids.includes(Number(serviceId));
        });

        if (eligible.length === 0) return null;
        const randomMember = eligible[Math.floor(Math.random() * eligible.length)];
        return randomMember.team_member_id || randomMember.id;
    };

    useEffect(() => {
        if (isMulti) {
            setLoadingMulti(true);
            setMultiSlots([]);
            setSelectedSlot(null);

            // Prepare payload with resolved IDs
            const servicesPayload = selectedServices.map(s => {
                const resolvedId = resolveEffectiveTeamMember(s.service_id, s.teamMember);
                return {
                    service_id: Number(s.service_id),
                    team_member_id: resolvedId // Must be integer or null (but API rejects null apparently)
                };
            }).filter(s => s.team_member_id !== null); // Filter out if no pro found (avoid API error)

            if (servicesPayload.length !== selectedServices.length) {
                console.warn("Could not find compatible pros for all services");
                setLoadingMulti(false);
                return;
            }

            createMultiConsecutiveSlots({
                body: {
                    date: selectedDate.format("YYYY-MM-DD"),
                    buffer_minutes: 0,
                    slot_interval_minutes: 30, // Default interval
                    allow_next_slot: true,
                    max_gap_minutes: 30,
                    services: servicesPayload
                }
            })
                .unwrap()
                .then(result => {
                    setMultiSlots(result?.chains || []);
                    setLoadingMulti(false);
                })
                .catch(err => {
                    console.error("Error fetching multi slots", err);
                    setMultiSlots([]);
                    setLoadingMulti(false);
                });
        }
    }, [selectedDate, isMulti, selectedServices, createMultiConsecutiveSlots, employees]);

    // --- Consolidated Slots List ---
    const currentSlots = useMemo(() => {
        if (isMulti) {
            // Return chains start times
            // A chain object looks like { start_time: "09:00", ... }
            return multiSlots;
        } else {
            // Single slots
            if (!singleSlotsData) return [];

            // Cas 1 : Créneaux directs (Team Member spécifique sélectionné)
            if (Array.isArray(singleSlotsData.available_slots)) {
                return singleSlotsData.available_slots;
            }

            // Cas 2 : Créneaux agrégés (Pas de Team Member spécifique -> "Peu importe")
            if (singleSlotsData.all_team_members_slots) {
                const allSlots = new Set();
                Object.values(singleSlotsData.all_team_members_slots).forEach(tmData => {
                    if (Array.isArray(tmData.available_slots)) {
                        tmData.available_slots.forEach(slot => allSlots.add(slot));
                    }
                });
                // Convertir en tableau et trier
                return Array.from(allSlots).sort();
            }

            return [];
        }
    }, [isMulti, multiSlots, singleSlotsData]);

    // Extract start time string from slot item
    const getSlotTime = (slot) => {
        return isMulti ? slot.start_time : slot;
    };

    // --- Grouping and Filtering Slots (Matin / Après-midi) ---
    const { morningSlots, afternoonSlots } = useMemo(() => {
        const morning = [];
        const afternoon = [];
        const now = new Date();
        const currentHour = now.getHours();
        const currentMinute = now.getMinutes();
        const currentDate = now.toISOString().split('T')[0]; // Format YYYY-MM-DD
        const selectedDateStr = selectedDate.format('YYYY-MM-DD');

        currentSlots.forEach(slot => {
            const time = getSlotTime(slot); // "HH:mm"
            if (!time) return;
            
            // Vérifier si le créneau est dans le futur avec une marge de 10 minutes
            const [hour, minute] = time.split(':').map(Number);
            const isToday = selectedDateStr === currentDate;
            
            // Si c'est aujourd'hui, vérifier l'heure
            if (isToday) {
                // Vérifier si l'heure est passée avec une marge de 10 minutes
                if (hour < currentHour || (hour === currentHour && minute < currentMinute + 10)) {
                    return; // Ignorer ce créneau
                }
            }
            
            // Ajouter au groupe approprié
            if (hour < 12) morning.push(slot);
            else afternoon.push(slot);
        });

        return { 
            morningSlots: morning, 
            afternoonSlots: afternoon 
        };
    }, [currentSlots, isMulti, selectedDate]); // Depend on currentSlots, isMulti et selectedDate

    // --- Selection Logic ---
    const handleSlotClick = (slot) => {
        setSelectedSlot(slot);
    };

    const isSlotSelected = (slot) => {
        const time = getSlotTime(slot);
        const selectedTime = selectedSlot ? getSlotTime(selectedSlot) : null;
        return time === selectedTime;
    };

    // --- Confirmation Logic ---
    const handleContinue = () => {
        if (!selectedSlot) return;
        onContinue({
            date: selectedDate.format("YYYY-MM-DD"),
            slot: selectedSlot // String time or Chain object
        });
    };

    const totalDuration = selectedServices.reduce((acc, s) => acc + (s.duration || 0), 0);
    const selectedTimeDisplay = selectedSlot ? getSlotTime(selectedSlot) : null;
    const isLoading = isMulti ? loadingMulti : isFetchingSingle;

    return (
        <Container
            data-testid="date-time-selection-theme-root"
            maxWidth="lg"
            style={{ fontFamily: theme.typography.fontFamily.sans.join(', ') }}
            sx={{
            py: { xs: 0, md: 0 },
            position: 'relative',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            [HIGH_DENSITY_TABLET_MEDIA]: {
                maxWidth: 'min(94vw, 1500px)',
            },
        }}>
            {/* Bouton Retour */}
            {showBackButton && (
                <Box sx={{ 
                    mb: { xs: 1, md: 3 },
                    display: 'flex',
                    justifyContent: 'flex-start',
                    width: '100%',
                    maxWidth: 1200,
                    mx: '0',
                    px: { xs: 0, md: 0 },
                    [HIGH_DENSITY_TABLET_MEDIA]: {
                        maxWidth: 1440,
                    },
                }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={onBack}
                        sx={{
                            color: theme.colors.gray[600],
                            textTransform: 'none',
                            fontWeight: 600,
                            pl: 0,
                            py: 0.5,
                            minWidth: 'auto',
                            '& .MuiButton-startIcon': {
                                marginRight: '4px',
                                marginLeft: '-4px'
                            },
                            '&:hover': {
                                backgroundColor: theme.colors.gray[100],
                            }
                        }}
                    >
                        {t('client_services.back')}
                    </Button>
                </Box>
            )}

            {/* Main Content : Grid 2 Columns */}
            <Box sx={{ flex: 1, width: '100%' }}>
                <Grid container spacing={{ xs: 2, md: 4 }} sx={{ maxWidth: 1200, mx: 'auto', width: '100%', px: { xs: 1, md: 0 }, [HIGH_DENSITY_TABLET_MEDIA]: { maxWidth: 1440 } }}>
                {/* Left: Calendar */}
                <Grid item xs={12} md={5}>
                    <Paper elevation={0} sx={{ p: { xs: 1, md: 2 }, borderRadius: 4, border: `1px solid ${theme.colors.gray[200]}`, [HIGH_DENSITY_TABLET_MEDIA]: { p: 3 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, pt: 1 }}>
                            {/* Custom Navigate Buttons if needed, DateCalendar handles it */}
                        </Box>
                        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
                            <DateCalendar
                                value={selectedDate}
                                onChange={(newValue) => {
                                    setSelectedDate(newValue);
                                    setSelectedSlot(null);
                                }}
                                disablePast
                                sx={{
                                    width: '100%',
                                    '& .MuiPickersDay-root': {
                                        borderRadius: '50%',
                                        '&.Mui-selected': { backgroundColor: `${theme.colors.primary[500]} !important` },
                                        '&:hover': { backgroundColor: theme.colors.primary[50] }
                                    },
                                    '& .MuiDayCalendar-weekDayLabel': {
                                        color: theme.colors.gray[400],
                                        fontWeight: 700
                                    }
                                }}
                            />
                        </LocalizationProvider>

                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: theme.colors.primary[500] }} />
                                <Typography variant="caption" color="text.secondary">{t('client_services.selected')}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: theme.colors.gray[200] }} />
                                <Typography variant="caption" color="text.secondary">{t('client_services.available')}</Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                {/* Right: Slots Grid */}
                <Grid item xs={12} md={7}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, borderRadius: 4, border: `1px solid ${theme.colors.gray[200]}`, minHeight: 400, [HIGH_DENSITY_TABLET_MEDIA]: { p: 5, minHeight: 500 } }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                                    {t('client_services.time_slots_for')} {selectedDate.locale(i18n.language === 'ar' ? 'ar' : i18n.language === 'en' ? 'en' : 'fr').format('dddd D MMMM')}
                                </Typography>

                                {isLoading && (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                                        <CircularProgress sx={{ color: theme.colors.primary[500] }} />
                                    </Box>
                                )}

                                {!isLoading && currentSlots.length === 0 && (
                                    <Box sx={{ textAlign: 'center', py: 6, color: theme.colors.gray[500] }}>
                                        <Typography>{t('client_services.no_slots_available')}</Typography>
                                    </Box>
                                )}

                                {!isLoading && currentSlots.length > 0 && (
                                    <>
                                        {/* Matin */}
                                        {morningSlots.length > 0 && (
                                            <Box sx={{ mb: 4 }}>
                                                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', color: theme.colors.gray[500], fontWeight: 700, mb: 2 }}>
                                                    <Box component="span" sx={{ mr: 1 }}>☀</Box> {t('client_services.morning')}
                                                </Typography>
                                                <Grid container spacing={1}>
                                                    {morningSlots.map((slot, i) => (
                                                        <Grid item xs={4} md={3} key={i}>
                                                            <Button
                                                                fullWidth
                                                                variant="outlined"
                                                                onClick={() => handleSlotClick(slot)}
                                                                sx={{
                                                                    py: { xs: 1, sm: 1.5 },
                                                                    px: { xs: 0.5, sm: 1 },
                                                                    borderRadius: 3,
                                                                    borderColor: isSlotSelected(slot) ? theme.colors.primary[500] : theme.colors.gray[200],
                                                                    backgroundColor: isSlotSelected(slot) ? theme.salon.primary : 'transparent',
                                                                    color: isSlotSelected(slot) ? (theme.salon.compatibilityMode ? 'white' : theme.salon.onPrimary) : theme.colors.gray[800],
                                                                    fontWeight: 600,
                                                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                                    [HIGH_DENSITY_TABLET_MEDIA]: {
                                                                        fontSize: '1rem',
                                                                        py: 1.7,
                                                                    },
                                                                    whiteSpace: 'nowrap',
                                                                    minWidth: 'auto',
                                                                    '&:hover': {
                                                                        borderColor: theme.colors.primary[500],
                                                                        backgroundColor: isSlotSelected(slot) ? theme.colors.primary[600] : theme.colors.primary[50]
                                                                    }
                                                                }}
                                                            >
                                                                {getSlotTime(slot)}
                                                            </Button>
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </Box>
                                        )}

                                        {/* Après-midi */}
                                        {afternoonSlots.length > 0 && (
                                            <Box>
                                                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', color: theme.colors.gray[500], fontWeight: 700, mb: 2 }}>
                                                    <Box component="span" sx={{ mr: 1 }}>☀</Box> {t('client_services.afternoon')}
                                                </Typography>
                                                <Grid container spacing={1}>
                                                    {afternoonSlots.map((slot, i) => (
                                                        <Grid item xs={4} md={3} key={i}>
                                                            <Button
                                                                fullWidth
                                                                variant="outlined"
                                                                onClick={() => handleSlotClick(slot)}
                                                                sx={{
                                                                    py: { xs: 1, sm: 1.5 },
                                                                    px: { xs: 0.5, sm: 1 },
                                                                    borderRadius: 3,
                                                                    borderColor: isSlotSelected(slot) ? theme.colors.primary[500] : theme.colors.gray[200],
                                                                    backgroundColor: isSlotSelected(slot) ? theme.salon.primary : 'transparent',
                                                                    color: isSlotSelected(slot) ? (theme.salon.compatibilityMode ? 'white' : theme.salon.onPrimary) : theme.colors.gray[800],
                                                                    fontWeight: 600,
                                                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                                                    [HIGH_DENSITY_TABLET_MEDIA]: {
                                                                        fontSize: '1rem',
                                                                        py: 1.7,
                                                                    },
                                                                    whiteSpace: 'nowrap',
                                                                    minWidth: 'auto',
                                                                    '&:hover': {
                                                                        borderColor: theme.colors.primary[500],
                                                                        backgroundColor: isSlotSelected(slot) ? theme.colors.primary[600] : theme.colors.primary[50]
                                                                    }
                                                                }}
                                                            >
                                                                {getSlotTime(slot)}
                                                            </Button>
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </Box>
                                        )}
                                    </>
                                )}
                            </Paper>

                            {/* Summary Card */}
                            {selectedSlot && (
                                <Paper
                                    elevation={0}
                                    sx={{
                                        mt: 3, p: 3,
                                        borderRadius: 4,
                                        backgroundColor: theme.colors.primary[50],
                                        border: `1px solid ${theme.colors.primary[100]}`,
                                        display: 'flex', flexDirection: 'column', gap: 2
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        <Box sx={{ bgcolor: theme.salon.primary, color: theme.salon.compatibilityMode ? 'white' : theme.salon.onPrimary, borderRadius: '50%', p: 1, display: 'flex' }}>
                                            <EventIcon />
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: theme.colors.primary[500], fontWeight: 700, letterSpacing: 1 }}>{t('client_services.appointment_summary')}</Typography>
                                            <Typography variant="h6" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
                                                {selectedDate.locale(i18n.language === 'ar' ? 'ar' : i18n.language === 'en' ? 'en' : 'fr').format('dddd D MMMM')} {t('client_services.at')} {selectedTimeDisplay}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: theme.colors.gray[500] }}>
                                                {t('client_services.total_duration')}: {Math.floor(totalDuration / 60)}h{totalDuration % 60 || '00'} • {t('client_services.at_salon')} {salonDetails?.name || 'GLOW Paris'}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <Button
                                        fullWidth
                                        variant="contained"
                                        size="large"
                                        onClick={handleContinue}
                                        endIcon={<ArrowForwardIcon />}
                                        sx={{
                                            fontWeight: 700,
                                        }}
                                    >
                                        {t('client_services.validate_continue')}
                                    </Button>
                                </Paper>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
                </Grid>
            </Box>
        </Container>
    );
};

export default DateTimeSelectionStep;
